import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { CardSliceContent } from '@app/code/src/types/prismic.types'
import { CreateFlowcodeForm } from '@app/code/src/components/Homepage/CreateFlowcodeForm'
import {
  FlowcodeTopSmallBannerResp,
  JumpMenu,
  PrismicAPIResp
} from '@app/code/src/components/Homepage/HomepageStaticContent/types'
import { FreeQrCodeGeneratorVariation1Document } from '@dtx-company/prismic-codegen/src/generated.types'
import { HomepageBelowFoldExperimentData } from '@app/code/src/components/Homepage/HomepageStaticContent/variants/types'
import { HomepageTopBanner } from '@app/code/src/components/Homepage/HomepageTopBanner'
import { HowToSchema } from '@app/code/src/components/Homepage/HowToSchema'
import { LOCGPrismicProvider } from '@app/code/src/components/LoggedOutCodeGeneratorV2/LOCGPrismicContext'
import { LoggedOutCodeGenerator } from '@app/code/src/components/LoggedOutCodeGeneratorV2/LoggedOutCodeGenerator'
import { Meta } from '../../../constants/meta'
import { NonGeneratorAboveFoldProps } from '@app/code/src/components/Homepage/NonGeneratorAboveFold/NonGeneratorAboveFold.types'
import { PageContainer } from '../../layout'
import { ProUpsellBanner } from '@app/code/src/components/Homepage/ProUpsellBanner'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useIsMobileOrTablet } from '@app/code/src/hooks/useIsMobileOrTablet'
import { useLoggedOutGeneratorV2Experiment } from '@app/code/src/components/LoggedOutCodeGeneratorV2/hooks/useLoggedOutGeneratorV2Experiment'
import LongAndColorful from '@app/code/src/components/Homepage/HomepageStaticContent/variants/V2-LongAndColorful'
import React, { useEffect } from 'react'
import dynamic from 'next/dynamic'

const NonGeneratorAboveFold = dynamic<NonGeneratorAboveFoldProps>(() =>
  import('@app/code/src/components/Homepage/NonGeneratorAboveFold/NonGeneratorAboveFold').then(
    mod => mod.NonGeneratorAboveFold
  )
)

export interface FreeQrCodeGeneratorPageProps {
  homepageData: PrismicAPIResp[]
  bannerData: { [key: string]: CardSliceContent }
  locgV2Data: FreeQrCodeGeneratorVariation1Document['data']
  homepageBelowFoldExperimentData: HomepageBelowFoldExperimentData
  nonGeneratorAboveFoldData: NonGeneratorAboveFoldProps
  injectedMeta?: Meta
  swrFallback?: Record<string, unknown>
}

export const FreeQrCodeGeneratorPage = (props: FreeQrCodeGeneratorPageProps): JSX.Element => {
  const { homepageData, bannerData, homepageBelowFoldExperimentData, injectedMeta } = props
  const { hideFooter } = useCodeSelector((state: RootCodeState) => {
    return state.settings
  })
  const showStaticContent = useCodeSelector(state => state.homePage.showStaticContent)

  const bannerSlice: FlowcodeTopSmallBannerResp | undefined = homepageData.find(
    slice => slice.slice_type === 'flowcode_top_small_banner'
  )
  const jumpMenu = homepageData.find(slice => slice.slice_type === 'jump_menu')
  const isMobileOrTablet = useIsMobileOrTablet()
  const LOCGVariant = useLoggedOutGeneratorV2Experiment()
  const displayLOCGV2 = LOCGVariant === 'new_locg_variant'
  const hideGenerator = LOCGVariant === 'removal_variant'

  /** Track what variation of LOGGED_OUT_CODE_GENERATOR_V2_TEST the user is viewing */
  useEffect(() => {
    if (displayLOCGV2) {
      fireAnalyticsEvent('locgtest_viewed_newlocg')
    } else if (hideGenerator) {
      fireAnalyticsEvent('locgtest_viewed_locgremoved')
    } else {
      fireAnalyticsEvent('locgtest_viewed_control')
    }
  }, [displayLOCGV2, hideGenerator])

  return (
    <PageContainer
      disableSideNav
      injectedMeta={injectedMeta}
      page={Routes.FREE_QR_CODE_GENERATOR}
      disableFooter={hideFooter}
    >
      <LOCGPrismicProvider
        homepageData={props.homepageData}
        bannerData={props.bannerData}
        locgV2Data={props.locgV2Data}
        homepageBelowFoldExperimentData={props.homepageBelowFoldExperimentData}
        nonGeneratorAboveFoldData={props.nonGeneratorAboveFoldData}
      >
        <Box display="block">
          {hideGenerator && (
            <>
              <NonGeneratorAboveFold {...props.nonGeneratorAboveFoldData} />
            </>
          )}

          {!hideGenerator && (
            <>
              {displayLOCGV2 ? (
                <LoggedOutCodeGenerator />
              ) : (
                <>
                  {bannerSlice?.primary?.header && (
                    <HomepageTopBanner
                      header={bannerSlice?.primary?.header}
                      backgroundColor={bannerSlice?.primary?.background_color}
                      display={isMobileOrTablet ? 'none' : 'block'}
                    />
                  )}
                  {jumpMenu && (
                    <HomepageTopBanner
                      jumpMenu={jumpMenu as JumpMenu}
                      backgroundColor={bannerSlice?.primary?.background_color}
                      display={isMobileOrTablet ? 'none' : 'block'}
                    />
                  )}

                  <CreateFlowcodeForm data={homepageData} />
                </>
              )}
            </>
          )}
          <Box display={['none', 'none', 'none', 'block']}>
            <ProUpsellBanner
              data={bannerData.proUpsellBannerCard}
              sectionId={'pro-upsell-banner'}
            />
          </Box>

          {showStaticContent && <LongAndColorful data={homepageBelowFoldExperimentData.v2} />}
        </Box>
      </LOCGPrismicProvider>
      <HowToSchema />
    </PageContainer>
  )
}
